import { useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ConfirmDeleteDialog } from '@/features/patient-record/components/_shared/ConfirmDeleteDialog';
import { useResolveUserDisplayNames } from '@/features/patient-record/components/panels/CareTeamPanel/utils';
import {
  useDeleteTask,
  useUpdateTask,
} from '@/features/patient-record/hooks/useTasksMutations';
import { useEditableDraft } from '@/features/patient-record/hooks/useEditableDraft';
import { useDirtyDialogGuard } from '@/features/patient-record/hooks/useDirtyDialogGuard';
import { patientQueries } from '@/features/patient-record/queries';
import { formatDate } from '@/lib/format';
import type {
  PatientTaskUpdateInput,
  PatientTaskViewModel,
} from '@/features/patient-record/types';

import { TaskFormFields } from './TaskFormFields';
import type { TaskFormValues } from './TaskFormFields.types';
import { EMPTY_FORM_VALUES } from './taskHelpers';

interface TaskEditDialogProps {
  readonly guid: string;
  readonly taskId: string;
  readonly onClose: () => void;
}

/**
 * Edit dialog (PTV2-1097). Fetches the full task via
 * `patientQueries.task(guid, id)` so description / notes / completion
 * metadata are populated, then submits a partial `PATCH` (only the keys
 * that actually changed go on the wire). Delete uses the soft-delete
 * endpoint behind a confirmation dialog.
 */
export function TaskEditDialog({
  guid,
  taskId,
  onClose,
}: TaskEditDialogProps): ReactElement {
  const { t } = useTranslation();
  const detail = useQuery(patientQueries.task(guid, taskId));
  const update = useUpdateTask(guid);
  const remove = useDeleteTask(guid);

  const initial = detail.data;
  const initialAssigneeGuid = initial?.assigneeId ?? null;
  // PTV2-1509: resolve the assignee and completer guids through one hook (same join +
  // fallback as the Care Team panel) so the raw guid is never rendered.
  const completedByGuid = initial?.status === 'DONE' ? (initial.completedBy ?? null) : null;
  const resolvedNames = useResolveUserDisplayNames(
    [initialAssigneeGuid, completedByGuid].filter((id): id is string => id !== null),
  );
  const resolvedAssignee =
    initialAssigneeGuid !== null ? resolvedNames.get(initialAssigneeGuid) : undefined;
  // Lookup in flight → blank label; lookup failed → "Unknown user".
  const assigneeLabel =
    resolvedAssignee?.status === 'ready'
      ? (resolvedAssignee.name ?? t('patient.careTeam.unknownUser'))
      : '';
  const initialValues = useMemo<TaskFormValues | null>(
    () =>
      initial !== null && initial !== undefined ? toFormValues(initial, assigneeLabel) : null,
    [initial, assigneeLabel],
  );

  const [confirmDelete, setConfirmDelete] = useState(false);

  const busy = update.isPending || remove.isPending;

  // Build the wire patch from the loaded server task + the current draft; only
  // the keys that actually changed go out (partial PATCH — same diff for both
  // the explicit submit and the confirm-prompt Save).
  const commit = (d: TaskFormValues): Promise<unknown> => {
    if (initial === null || initial === undefined) return Promise.resolve();
    const patch = diff(initial, d);
    if (Object.keys(patch).length === 0) return Promise.resolve();
    return update.mutateAsync({ taskId, input: patch });
  };

  // PTV2-1327: one primitive owns the draft. Explicit-submit dialog ⇒ autosave off.
  // Seeded from a blank placeholder until the detail query resolves; reseed-safety
  // then adopts the loaded values (and the resolved assignee name) while untouched.
  const form = useEditableDraft<TaskFormValues>({
    initial: initialValues ?? EMPTY_FORM_VALUES,
    label: t('patient.tasks.editTitle'),
    autosave: false,
    canSave: (d) => initial !== null && initial !== undefined && d.title.trim().length > 0,
    onSave: (d) => commit(d),
  });
  const { draft } = form;

  // Explicit submit: commit AND close. An empty diff is a no-op close.
  const submitAndClose = (): void => {
    if (!form.canSave || initial === null || initial === undefined) return;
    const patch = diff(initial, draft);
    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }
    void update.mutateAsync({ taskId, input: patch }).then(onClose).catch(() => undefined);
  };

  // PTV2-1301: guard dismiss / refresh while there are unsaved edits. The primitive
  // already registers, so the guard only intercepts dismissal + renders the confirm.
  const guard = useDirtyDialogGuard({
    dirty: form.isDirty,
    canSave: form.canSave,
    saving: busy,
    label: t('patient.tasks.editTitle'),
    onSave: submitAndClose,
    onClose,
    register: false,
  });

  // No completer → "system"; lookup in flight → date only; lookup failed → "Unknown user".
  let completedLine: string | null = null;
  if (initial?.status === 'DONE' && initial.completedAt !== null) {
    const date = formatDate(initial.completedAt);
    const completer = completedByGuid !== null ? resolvedNames.get(completedByGuid) : undefined;
    if (completedByGuid === null) {
      completedLine = t('patient.tasks.completedMeta', {
        date,
        author: t('patient.tasks.unknownActor'),
      });
    } else if (completer === undefined || completer.status === 'pending') {
      completedLine = t('patient.tasks.completedMetaDateOnly', { date });
    } else {
      completedLine = t('patient.tasks.completedMeta', {
        date,
        author: completer.name ?? t('patient.careTeam.unknownUser'),
      });
    }
  }

  return (
    <>
      <Dialog open {...guard.rootProps}>
        <DialogContent className="max-w-2xl gap-3" {...guard.contentProps}>
          <DialogHeader>
            <DialogTitle>{t('patient.tasks.editTitle')}</DialogTitle>
            <DialogDescription>{t('patient.tasks.editDescription')}</DialogDescription>
          </DialogHeader>

          {detail.isPending && (
            <p className="text-sm text-muted-foreground">{t('patient.tasks.loadingDetail')}</p>
          )}

          {detail.isError && (
            <p role="alert" className="text-sm text-destructive">
              {t('patient.tasks.loadError')}
            </p>
          )}

          {initial !== null && initial !== undefined && (
            <form
              ref={form.containerProps.ref}
              className="space-y-3"
              onSubmit={(event) => {
                event.preventDefault();
                submitAndClose();
              }}
            >
              <TaskFormFields
                values={draft}
                onChange={(next) => {
                  form.setDraft(next);
                }}
                showStatus
                disabled={busy}
              />

              {completedLine !== null && (
                <p className="text-[11.5px] text-muted-foreground">{completedLine}</p>
              )}

              {(update.isError || remove.isError) && (
                <p role="alert" className="text-xs text-destructive">
                  {t('patient.tasks.errorSave')}
                </p>
              )}

              <DialogFooter className="flex items-center justify-between gap-2 pt-2 sm:justify-between">
                <Button
                  type="button"
                  variant="ghost"
                  className="text-destructive hover:text-destructive"
                  onClick={() => {
                    setConfirmDelete(true);
                  }}
                  disabled={busy}
                >
                  <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                  {t('patient.tasks.delete')}
                </Button>
                <div className="flex items-center gap-2">
                  <Button type="button" variant="ghost" onClick={guard.requestClose} disabled={busy}>
                    {t('patient.tasks.cancel')}
                  </Button>
                  <Button type="submit" disabled={!form.canSave || busy}>
                    {update.isPending ? t('patient.tasks.saving') : t('patient.tasks.save')}
                  </Button>
                </div>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {guard.confirm}

      {confirmDelete && (
        <ConfirmDeleteDialog
          title={t('patient.tasks.deleteConfirmTitle')}
          message={t('patient.tasks.deleteConfirmMessage', {
            title: initial?.title ?? t('patient.tasks.unknownTask'),
          })}
          confirmLabel={t('patient.tasks.delete')}
          busy={remove.isPending}
          onCancel={() => {
            setConfirmDelete(false);
          }}
          onConfirm={() => {
            remove.mutate(
              { taskId },
              {
                onSuccess: () => {
                  setConfirmDelete(false);
                  onClose();
                },
              },
            );
          }}
        />
      )}
    </>
  );
}

function toFormValues(task: PatientTaskViewModel, assigneeLabel: string): TaskFormValues {
  return {
    title: task.title,
    type: task.type,
    priority: task.priority,
    status: task.status,
    dueDate: task.dueDate ?? '',
    description: task.description ?? '',
    notes: task.notes ?? '',
    // Never label the picker with the raw guid: the label is blank while the lookup
    // is in flight (reseed swaps in the name) and "Unknown user" if it failed. The
    // guid stays on the option so `diff()` compares identities, not labels.
    assignee:
      task.assigneeId !== null
        ? {
            guid: task.assigneeId,
            fullName: assigneeLabel,
            subtitle: null,
          }
        : null,
  };
}

function diff(initial: PatientTaskViewModel, values: TaskFormValues): PatientTaskUpdateInput {
  const patch: PatientTaskUpdateInput = {};
  const titleNext = values.title.trim();
  if (titleNext !== initial.title) Object.assign(patch, { title: titleNext });
  if (values.type !== initial.type) Object.assign(patch, { type: values.type });
  if (values.priority !== initial.priority) Object.assign(patch, { priority: values.priority });
  if (values.status !== initial.status) Object.assign(patch, { status: values.status });
  const assigneeNext = values.assignee?.guid ?? null;
  if (assigneeNext !== initial.assigneeId) Object.assign(patch, { assigneeId: assigneeNext });
  const dueNext = values.dueDate.length > 0 ? values.dueDate : null;
  if (dueNext !== initial.dueDate) Object.assign(patch, { dueDate: dueNext });
  const descNext = values.description.trim().length > 0 ? values.description.trim() : null;
  if (descNext !== initial.description) Object.assign(patch, { description: descNext });
  const notesNext = values.notes.trim().length > 0 ? values.notes.trim() : null;
  if (notesNext !== initial.notes) Object.assign(patch, { notes: notesNext });
  return patch;
}
